import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import SharedNavigation from "@/components/SharedNavigation";

const HowToGetFeaturedPage = () => {
  const [currentUser, setCurrentUser] = useState(null);

  return (
    <>
      <Helmet>
        <title>How to Get Featured | RGM Studio</title>
        <meta name="description" content="Learn how to get featured on RGM Studio, earn badges, and grow your music career." />
      </Helmet>
      
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={currentUser} activePage="featured-guide" />
        
        <main className="pt-20 pb-16 px-4">
          <div className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
                How to Get <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">Featured</span>
              </h1>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Your guide to earning recognition, badges, and spotlight placement on RGM Studio
              </p>
            </div>

            {/* Weekly Spotlight Section */}
            <Card className="bg-gradient-to-br from-purple-900/30 to-black border-purple-500/30 p-8 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center text-3xl flex-shrink-0">
                  🏆
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-3">Weekly Spotlight</h2>
                  <p className="text-gray-300 mb-4">
                    Every week, our A&R team selects the top 3 tracks to feature in our Weekly Spotlight. 
                    Winners receive the prestigious <span className="text-yellow-400 font-semibold">Spotlight Winner</span> badge 
                    and increased visibility across the platform.
                  </p>
                  <div className="bg-black/40 rounded-xl p-4 space-y-3">
                    <h3 className="text-white font-semibold">How to qualify:</h3>
                    <ul className="space-y-2 text-gray-400">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        Submit high-quality, original music
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        Tracks must be "Featured" or "Shortlisted" status
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        Strong production quality, lyrics, and originality
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        Complete artist profile with bio and social links
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            {/* Badges Section */}
            <Card className="bg-gradient-to-br from-blue-900/30 to-black border-blue-500/30 p-8 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-3xl flex-shrink-0">
                  ✓
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-3">Earning Badges</h2>
                  <p className="text-gray-300 mb-4">
                    Badges showcase your achievements and build credibility with listeners and industry professionals.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-black/40 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-600 text-white">⭐ Founding Artist</span>
                      </div>
                      <p className="text-sm text-gray-400">Awarded to early supporters who joined during our launch phase.</p>
                    </div>
                    
                    <div className="bg-black/40 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white">🏆 Spotlight Winner</span>
                      </div>
                      <p className="text-sm text-gray-400">Won a top 3 position in the Weekly Spotlight competition.</p>
                    </div>
                    
                    <div className="bg-black/40 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white">🔥 Spotlight Artist</span>
                      </div>
                      <p className="text-sm text-gray-400">Track featured in a weekly or monthly spotlight.</p>
                    </div>
                    
                    <div className="bg-black/40 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-cyan-500 to-blue-500 text-white">🎵 Playlist Pick</span>
                      </div>
                      <p className="text-sm text-gray-400">Track added to an official RGM Studio playlist.</p>
                    </div>
                    
                    <div className="bg-black/40 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-400 to-emerald-500 text-white">📈 Rising Artist</span>
                      </div>
                      <p className="text-sm text-gray-400">Showing significant growth in plays and engagement.</p>
                    </div>
                    
                    <div className="bg-black/40 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-blue-500 text-white">✓ Verified</span>
                      </div>
                      <p className="text-sm text-gray-400">Identity and artistry verified by RGM Studio team.</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Getting Featured Section */}
            <Card className="bg-gradient-to-br from-green-900/30 to-black border-green-500/30 p-8 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-3xl flex-shrink-0">
                  ⭐
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-3">How Artists Get Featured</h2>
                  <p className="text-gray-300 mb-4">
                    Our team reviews all submissions and selects standout tracks for feature placement.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold flex-shrink-0">1</div>
                      <div>
                        <h3 className="text-white font-semibold">Submit Your Music</h3>
                        <p className="text-gray-400 text-sm">Upload your best track with high-quality audio and artwork.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold flex-shrink-0">2</div>
                      <div>
                        <h3 className="text-white font-semibold">A&R Review</h3>
                        <p className="text-gray-400 text-sm">Our team scores tracks on lyrics, flow, originality, mix quality, branding, and growth potential.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold flex-shrink-0">3</div>
                      <div>
                        <h3 className="text-white font-semibold">Status Update</h3>
                        <p className="text-gray-400 text-sm">Top submissions are marked as "Featured" or "Shortlisted" for spotlight consideration.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold flex-shrink-0">4</div>
                      <div>
                        <h3 className="text-white font-semibold">Feature Placement</h3>
                        <p className="text-gray-400 text-sm">Featured tracks appear in Discovery, Charts, and may be selected for Weekly Spotlight.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Verification Section */}
            <Card className="bg-gradient-to-br from-cyan-900/30 to-black border-cyan-500/30 p-8 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-3xl flex-shrink-0">
                  🔒
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-3">Artist Verification</h2>
                  <p className="text-gray-300 mb-4">
                    Verified artists receive a blue checkmark badge, increased visibility, and access to exclusive features.
                  </p>
                  
                  <div className="bg-black/40 rounded-xl p-4 space-y-3 mb-4">
                    <h3 className="text-white font-semibold">Requirements for Verification:</h3>
                    <ul className="space-y-2 text-gray-400">
                      <li className="flex items-start gap-2">
                        <span className="text-cyan-400">✓</span>
                        Complete profile with bio, photo, and social links
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-cyan-400">✓</span>
                        At least 2 approved/featured tracks on the platform
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-cyan-400">✓</span>
                        Consistent brand presence across social media
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-cyan-400">✓</span>
                        No community guideline violations
                      </li>
                    </ul>
                  </div>
                  
                  <Link to="/dashboard">
                    <Button className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white">
                      Request Verification
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>

            {/* CTA Section */}
            <div className="text-center mt-12">
              <h2 className="text-2xl font-bold text-white mb-4">Ready to Get Started?</h2>
              <p className="text-gray-400 mb-6">Submit your music today and take the first step toward getting featured.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/submit">
                  <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-8">
                    Submit Music
                  </Button>
                </Link>
                <Link to="/discover">
                  <Button size="lg" variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10 px-8">
                    Browse Featured
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default HowToGetFeaturedPage;
